import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI, AdminUser, DailySummary, MealItem } from '../api';
import { useAuth } from '../contexts/AuthContext';
import DayDetail from '../components/DayDetail';
import ConversationDetail from '../components/ConversationDetail';
import { formatDateTime, formatShortDate } from '../utils/dateUtils';

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { logout, user: currentUser } = useAuth();

  const [user, setUser] = useState<AdminUser | null>(null);
  const [dailySummaries, setDailySummaries] = useState<DailySummary[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dayItems, setDayItems] = useState<MealItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDayLoading, setIsDayLoading] = useState(false);
  const [error, setError] = useState('');
  const [conversationDate, setConversationDate] = useState<string | null>(null);

  // Targets editing state
  const [isEditingTargets, setIsEditingTargets] = useState(false);
  const [targetCarbs, setTargetCarbs] = useState('');
  const [targetProtein, setTargetProtein] = useState('');
  const [targetFat, setTargetFat] = useState('');
  const [isSavingTargets, setIsSavingTargets] = useState(false);

  useEffect(() => {
    if (userId) {
      loadUserDetails(parseInt(userId));
    }
  }, [userId]);

  const loadUserDetails = async (id: number) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await adminAPI.getUserDetails(id);
      if (response.success) {
        setUser(response.user);
        setDailySummaries(response.dailySummaries);
      } else {
        setError(response.error || 'Failed to load user details');
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('You do not have access to this user.');
      } else {
        setError('Failed to load user details');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadDayDetails = async (date: string) => {
    if (!userId) return;
    setIsDayLoading(true);
    try {
      const response = await adminAPI.getUserDayDetails(parseInt(userId), date);
      if (response.success) {
        setDayItems(response.items);
        setSelectedDate(date);
      }
    } catch (err) {
      console.error('Failed to load day details:', err);
    } finally {
      setIsDayLoading(false);
    }
  };

  const handleCloseDayDetail = () => {
    setSelectedDate(null);
    setDayItems([]);
  };

  const handleMealUpdated = () => {
    // Refresh day details and user details
    if (selectedDate) {
      loadDayDetails(selectedDate);
    }
    if (userId) {
      loadUserDetails(parseInt(userId));
    }
  };

  const startEditingTargets = () => {
    setTargetCarbs(user?.target_carbs?.toString() || '');
    setTargetProtein(user?.target_protein?.toString() || '');
    setTargetFat(user?.target_fat?.toString() || '');
    setIsEditingTargets(true);
  };

  const cancelEditingTargets = () => {
    setIsEditingTargets(false);
  };

  const saveTargets = async () => {
    if (!userId) return;
    setIsSavingTargets(true);
    try {
      const response = await adminAPI.updateUserTargets(parseInt(userId), {
        carbs: targetCarbs ? parseFloat(targetCarbs) : null,
        protein: targetProtein ? parseFloat(targetProtein) : null,
        fat: targetFat ? parseFloat(targetFat) : null,
      });
      if (response.success && response.user) {
        setUser(response.user);
        setIsEditingTargets(false);
      } else {
        alert(response.error || 'Failed to save targets');
      }
    } catch (err) {
      alert('Failed to save targets');
    } finally {
      setIsSavingTargets(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-blue-600 hover:underline"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-900"
            >
              &larr; Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">User Details</h1>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">{currentUser?.email}</span>
            <button
              onClick={logout}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {user?.email}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-500">User ID:</span>
              <span className="ml-2 font-medium">{user?.id}</span>
            </div>
            <div>
              <span className="text-gray-500">Created:</span>
              <span className="ml-2 font-medium">{formatDateTime(user?.created_at || null)}</span>
            </div>
            <div>
              <span className="text-gray-500">Last Login:</span>
              <span className="ml-2 font-medium">{formatDateTime(user?.last_login || null)}</span>
            </div>
          </div>
        </div>

        {/* Daily Targets */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Daily Macro Targets</h3>
            {!isEditingTargets && (
              <button
                onClick={startEditingTargets}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Edit
              </button>
            )}
          </div>

          {isEditingTargets ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-blue-600 mb-1">
                    Carbs (g)
                  </label>
                  <input
                    type="number"
                    value={targetCarbs}
                    onChange={(e) => setTargetCarbs(e.target.value)}
                    placeholder="Not set"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-green-600 mb-1">
                    Protein (g)
                  </label>
                  <input
                    type="number"
                    value={targetProtein}
                    onChange={(e) => setTargetProtein(e.target.value)}
                    placeholder="Not set"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-amber-600 mb-1">
                    Fat (g)
                  </label>
                  <input
                    type="number"
                    value={targetFat}
                    onChange={(e) => setTargetFat(e.target.value)}
                    placeholder="Not set"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelEditingTargets}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={saveTargets}
                  disabled={isSavingTargets}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
                >
                  {isSavingTargets ? 'Saving...' : 'Save Targets'}
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {user?.target_carbs != null ? `${user.target_carbs}g` : '—'}
                </div>
                <div className="text-xs text-blue-600">Carbs</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {user?.target_protein != null ? `${user.target_protein}g` : '—'}
                </div>
                <div className="text-xs text-green-600">Protein</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-amber-600">
                  {user?.target_fat != null ? `${user.target_fat}g` : '—'}
                </div>
                <div className="text-xs text-amber-600">Fat</div>
              </div>
            </div>
          )}
        </div>

        {/* Daily Summaries */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Daily Activity</h3>
            <p className="text-sm text-gray-500">Click a date to view and edit meal items</p>
          </div>

          {dailySummaries.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No meal data recorded yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-blue-600 uppercase tracking-wider">
                      Carbs
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-green-600 uppercase tracking-wider">
                      Protein
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-amber-600 uppercase tracking-wider">
                      Fat
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-purple-600 uppercase tracking-wider">
                      Calories
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dailySummaries.map((summary) => (
                    <tr
                      key={summary.date}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatShortDate(summary.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-blue-600">
                        {Math.round(summary.total_carbs)}g
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                        {Math.round(summary.total_protein)}g
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-amber-600">
                        {Math.round(summary.total_fat)}g
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-purple-600 font-medium">
                        {Math.round(summary.total_calories)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                        {summary.item_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <button
                          onClick={() => loadDayDetails(summary.date)}
                          className="text-blue-600 hover:text-blue-800 mr-3"
                        >
                          Meals
                        </button>
                        <button
                          onClick={() => setConversationDate(summary.date)}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          Input
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Day Detail Modal */}
      {selectedDate && userId && (
        <DayDetail
          userId={parseInt(userId)}
          date={selectedDate}
          items={dayItems}
          isLoading={isDayLoading}
          onClose={handleCloseDayDetail}
          onMealUpdated={handleMealUpdated}
        />
      )}

      {/* Conversation Detail Modal (Debug View) */}
      {conversationDate && userId && (
        <ConversationDetail
          userId={parseInt(userId)}
          date={conversationDate}
          onClose={() => setConversationDate(null)}
        />
      )}
    </div>
  );
};

export default UserDetail;
